import React, { useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import MinimalLayout from '../components/layout/MinimalLayout';
import { 
  CameraIcon, 
  UserCircleIcon, 
  XMarkIcon
} from '@heroicons/react/24/outline';

interface TeacherPhotoSetupData {
  email?: string;
  userType?: 'teacher' | 'student';
  skipEmailVerification?: boolean;
  profilePicture?: string;
}

// Removed unused constants - only keeping what's needed for photo upload step

const TeacherPhotoSetupPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Get data from navigation state
  const setupData = location.state as TeacherPhotoSetupData || {};
  const { email, userType = 'teacher', skipEmailVerification = false } = setupData;
  
  const [formData, setFormData] = useState<TeacherPhotoSetupData>({
    ...setupData
  });
  
  const [profilePicture, setProfilePicture] = useState<string | null>(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const TOTAL_STEPS = 1; // Just photo upload, then go to dashboard

  console.log('Teacher photo setup page loaded with email:', email, 'Step:', currentStep);

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select a valid image file.');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image size must be less than 5MB.');
      return;
    }

    setIsUploadingImage(true);
    console.log('Uploading profile picture:', file.name);

    try {
      // Simulate image upload
      const imageUrl = URL.createObjectURL(file);
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      setProfilePicture(imageUrl);
      setFormData(prev => ({ ...prev, profilePicture: imageUrl }));
      console.log('Profile picture uploaded successfully');
      
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Failed to upload image. Please try again.');
    } finally {
      setIsUploadingImage(false);
    }
  };

  const triggerImageUpload = () => {
    fileInputRef.current?.click();
  };

  const removeProfilePicture = () => {
    setProfilePicture(null);
    setFormData(prev => ({ ...prev, profilePicture: undefined }));
    console.log('Profile picture removed');
  };

  // Removed unused form handlers - only photo upload is needed

  const handleNext = () => {
    // For step 1 (photo upload), no validation required - photo is optional
    console.log('🔧 TeacherPhotoSetupPage - Moving from photo upload to dashboard');
    
    if (currentStep < TOTAL_STEPS) {
      setCurrentStep(prev => prev + 1);
    } else {
      handleSubmit();
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    } else {
      navigate('/email-verification', { state: { email, userType } });
    }
  };

  const handleSubmit = () => {
    console.log('🔧 TeacherPhotoSetupPage - Photo upload complete, navigating to dashboard');
    console.log('Photo setup data:', formData);
    
    // TODO: Save profile photo to backend
    // await updateTeacherProfilePhoto(formData.profilePicture);
    
    // Navigate to teacher dashboard with basic profile info
    navigate('/teacher-dashboard', { 
      state: { 
        teacherData: {
          email: email,
          profilePicture: formData.profilePicture,
          profilePhotoComplete: true,
          needsQuestionnaire: true  // Flag to show questionnaire completion option
        }
      } 
    });
  };

  const renderStepContent = () => {
    // Only show photo upload step
    return (
      <div className="text-center">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-white mb-3">Add Your Profile Photo</h2>
          <p className="text-slate-400 text-sm mb-8">
            Help students connect with you by adding a friendly profile picture.
          </p>
        </div>

        {/* Photo Upload Section */}
        <div className="flex flex-col items-center mb-8">
          <div className="relative mb-6">
            {profilePicture ? (
              <img 
                src={profilePicture} 
                alt="Profile Preview" 
                className="w-32 h-32 rounded-full border-4 border-yellow-500 object-cover"
              />
            ) : (
              <UserCircleIcon className="w-32 h-32 text-slate-500" />
            )}
            
            {/* Upload Button */}
            <button
              onClick={triggerImageUpload}
              disabled={isUploadingImage}
              className="absolute bottom-0 right-0 bg-yellow-500 hover:bg-yellow-600 text-slate-900 p-3 rounded-full shadow-lg transition-colors disabled:opacity-50"
            >
              {isUploadingImage ? (
                <div className="animate-spin w-5 h-5 border-2 border-slate-900 border-t-transparent rounded-full" />
              ) : (
                <CameraIcon className="w-5 h-5" />
              )}
            </button>
            
            {/* Remove Button */}
            {profilePicture && !isUploadingImage && (
              <button
                onClick={removeProfilePicture}
                className="absolute -top-2 -right-2 bg-red-500 hover:bg-red-600 text-white p-1 rounded-full shadow-lg transition-colors"
              >
                <XMarkIcon className="w-4 h-4" />
              </button>
            )}
          </div>
          
          <p className="text-slate-400 text-sm text-center max-w-sm">
            {profilePicture 
              ? "Looking great! You can always change this later in your profile settings."
              : "Click the camera icon to upload a photo. Students love seeing who they'll be learning from!"
            }
          </p>
        </div>
      </div>
    );
  };

  return (
    <MinimalLayout noPadding>
      <div className="min-h-screen bg-slate-900 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl w-full">
          <div className="bg-slate-800 p-8 rounded-xl shadow-2xl">
            
            {/* Progress Indicator */}
            <div className="mb-8">
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm text-slate-400">Step {currentStep} of {TOTAL_STEPS}</span>
                <span className="text-sm text-slate-400">{Math.round((currentStep / TOTAL_STEPS) * 100)}% Complete</span>
              </div>
              {currentStep === 1 && (
                <p className="text-xs text-slate-500 text-center mb-4">
                  📸 Add your profile photo and get started!
                </p>
              )}
              <div className="w-full bg-slate-700 rounded-full h-2">
                <div 
                  className="bg-yellow-500 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${(currentStep / TOTAL_STEPS) * 100}%` }}
                />
              </div>
            </div>

            {/* Step Content */}
            {renderStepContent()}

            {/* Navigation Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 mt-8">
              <button
                onClick={handleBack}
                className="flex-1 border border-slate-600 text-slate-300 hover:text-slate-100 hover:border-slate-500 font-semibold py-3 px-6 rounded-lg transition-colors"
              >
                {currentStep === 1 ? '← Back to Email' : '← Previous'}
              </button>
              
              <button
                onClick={handleNext}
                className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-slate-900 font-semibold py-3 px-6 rounded-lg transition-colors"
              >
                {currentStep === TOTAL_STEPS ? 'Continue to Dashboard' : 'Next →'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleImageUpload}
        className="hidden"
      />
    </MinimalLayout>
  );
};

export default TeacherPhotoSetupPage; 